/*
 * Renewal i18n — 콘텐츠 언어의 공개 API.
 *
 * 마크업은 `data-i18n="키"` 를 그대로 들고 있고, scripts/apply-i18n-pages.mjs 가
 * 빌드 때 그 요소에 `v-html="$rt('키')"` 바인딩을 달아 준다. 실제 문자열은
 * data/i18n-dist/{페이지}/{locale}.json 에서 온다.
 *
 * 호스트 앱(frontend/main)은 이 파일의 `useRenewalLocale().setRenewalLocale(locale)`
 * 한 줄만 호출하면 된다. renewal 의 DOM 이나 CSS 를 건드릴 필요가 없다.
 *
 * 상태를 모듈 변수가 아니라 Nuxt 의 useState 로 들고 있는 이유: 모듈 변수는 SSR 에서
 * 요청 간에 공유돼 다른 사용자의 언어가 새어 나갈 수 있다. useState 는 요청별로 분리된다.
 */
import { onMounted, ref } from 'vue'
import { useState } from '#app'

export const RENEWAL_LOCALES = ['ko', 'en', 'ja', 'vi', 'es'] as const
export type RenewalLocale = (typeof RENEWAL_LOCALES)[number]

/** 번역이 없을 때 돌아갈 언어. renewal 콘텐츠의 원문이 한국어다. */
export const RENEWAL_FALLBACK_LOCALE: RenewalLocale = 'ko'

export type RenewalDictionaries = Partial<Record<RenewalLocale, Record<string, string>>>

/**
 * 호스트가 넘긴 locale 문자열을 지원 언어로 정규화한다.
 * `ko-KR` 처럼 지역이 붙어도 받고, 모르는 값은 폴백으로 떨어뜨린다.
 */
export function normalizeRenewalLocale(input: unknown): RenewalLocale {
  const base = String(input ?? '').toLowerCase().split('-')[0]
  return (RENEWAL_LOCALES as readonly string[]).includes(base)
    ? (base as RenewalLocale)
    : RENEWAL_FALLBACK_LOCALE
}

/** 현재 콘텐츠 언어와 설정 함수. setup 안에서 호출해야 한다. */
export function useRenewalLocale() {
  const locale = useState<RenewalLocale>('renewal-locale', () => RENEWAL_FALLBACK_LOCALE)
  const setRenewalLocale = (next: unknown) => {
    locale.value = normalizeRenewalLocale(next)
    return locale.value
  }
  return { locale, setRenewalLocale }
}

/**
 * v-html 서브트리를 hydration 이후 한 번만 새로 만들기 위한 키.
 *
 * 호스트 앱은 기본 언어로 정적 프리렌더를 한 뒤, 클라이언트에서 쿠키 언어로 바꾼다.
 * 이때 `{{ }}` 텍스트는 hydration 이 교정하지만 **v-html 의 innerHTML 은 교정되지 않는다**
 * (Vue 는 hydration 에서 innerHTML prop 을 다시 쓰지 않는다). renewal 콘텐츠는 줄바꿈과
 * <strong>·<a> 때문에 전부 v-html 이라, 그대로 두면 본문만 프리렌더 언어로 남는다.
 *
 * 그래서 서버가 그린 언어를 payload 로 넘겨 두고, 클라이언트 언어가 다를 때만
 * 마운트 직후 키를 바꿔 서브트리를 한 번 다시 만든다. 같은 언어면 아무 일도 하지 않는다.
 */
export function useRenewalHydrationKey() {
  const { locale } = useRenewalLocale()
  const renderedLocale = useState<RenewalLocale | null>('renewal-rendered-locale', () => null)
  if (import.meta.server) renderedLocale.value = locale.value

  const key = ref('renewal')
  onMounted(() => {
    if (renderedLocale.value && renderedLocale.value !== locale.value) {
      key.value = `renewal-${locale.value}`
    }
  })
  return key
}

/**
 * 페이지 사전을 받아 `$rt(key)` 를 돌려준다.
 * 사전은 content 컴포넌트가 자기 페이지 것만 정적 import 해서 넘긴다
 * (전 페이지 사전을 한꺼번에 싣지 않으려고).
 */
export function useRenewalText(dictionaries: RenewalDictionaries) {
  const { locale } = useRenewalLocale()

  return (key: string): string => {
    const translated = dictionaries[locale.value]?.[key]
    if (translated) return translated
    // 빈 문자열도 "번역 없음"으로 본다 — 빈 칸이 원문을 지우는 것보다 낫다.
    return dictionaries[RENEWAL_FALLBACK_LOCALE]?.[key] ?? ''
  }
}
